import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

/**
 * M-D — Patient-facing purpose picker.
 *
 * Renders the four canonical purpose child threads for a given
 * conversation_group. The SERVER is authoritative for eligibility,
 * participants, and existence. This component only renders the
 * server's response and issues a POST /messaging/purposes/{id}/resolve
 * when the patient selects an "available" purpose.
 *
 * Never surfaces raw group IDs / thread UUIDs / role codes to the
 * patient — i18n-backed labels only (INV-D19).
 */
const PurposePicker = ({ conversationGroupId, currentThreadId, onSelectThread }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, purposes: [], error: null });
  const [resolving, setResolving] = useState(null);
  const lastFetchedGroupRef = React.useRef(null);

  const load = useCallback(async () => {
    if (!conversationGroupId) return;
    // M-D — de-dupe: skip if we already fetched THIS group this render.
    if (lastFetchedGroupRef.current === conversationGroupId && !state.loading) return;
    lastFetchedGroupRef.current = conversationGroupId;
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/messaging/purposes/${conversationGroupId}`);
      setState({ loading: false, purposes: r.data.purposes || [], error: null });
    } catch (err) {
      setState({ loading: false, purposes: [], error: err?.response?.data?.detail || 'load failed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationGroupId]);

  useEffect(() => {
    // Reset the fetch memo when the group actually changes so a real
    // group switch always re-fetches.
    if (lastFetchedGroupRef.current !== conversationGroupId) {
      lastFetchedGroupRef.current = null;
    }
    load();
  }, [conversationGroupId, load]);

  const resolveAndOpen = async (purpose) => {
    setResolving(purpose);
    try {
      const r = await api.post(
        `/messaging/purposes/${conversationGroupId}/resolve`,
        { purpose },
      );
      if (r.data?.thread) {
        onSelectThread(r.data.thread);
        // refresh so the newly created thread shows up as `exists`
        load();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'failed to open conversation';
      toast({ description: detail, variant: 'destructive' });
    } finally {
      setResolving(null);
    }
  };

  if (state.loading || !conversationGroupId) return null;

  return (
    <div
      className="border-b bg-slate-50 px-4 py-3 space-y-2"
      data-testid="purpose-picker"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {t('provider.messaging.purposes.sectionTitle')}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {state.purposes.map((p) => {
          const label = t(`provider.messaging.purposes.${p.purpose}`);
          const isCurrent = currentThreadId && p.thread_id === currentThreadId;
          if (p.status === 'unavailable') {
            return (
              <div
                key={p.purpose}
                className="rounded-lg border border-dashed border-slate-300 bg-white p-2 text-center opacity-60"
                data-testid={`purpose-tile-${p.purpose}-unavailable`}
                title={t('provider.messaging.purposes.unavailableHint')}
              >
                <div className="text-sm font-medium text-slate-500">{label}</div>
                <div className="text-[10px] text-slate-400">
                  {t('provider.messaging.purposes.unavailable')}
                </div>
              </div>
            );
          }
          return (
            <button
              key={p.purpose}
              onClick={() => {
                if (p.thread_id) {
                  onSelectThread({ id: p.thread_id });
                } else {
                  resolveAndOpen(p.purpose);
                }
              }}
              disabled={resolving === p.purpose}
              data-testid={`purpose-tile-${p.purpose}`}
              className={`relative rounded-lg border p-2 text-center transition ${
                isCurrent
                  ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                  : 'border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/40'
              }`}
            >
              {/* M-D — per-purpose unread badge (B-2 projection). */}
              {p.unread_count > 0 && (
                <span
                  data-testid={`purpose-tile-${p.purpose}-unread`}
                  className="absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-semibold text-white"
                >
                  {p.unread_count > 99 ? '99+' : p.unread_count}
                </span>
              )}
              <div className="text-sm font-medium text-slate-900">{label}</div>
              <div className="text-[10px] text-slate-500">
                {p.thread_id
                  ? t('provider.messaging.purposes.openBtn')
                  : t('provider.messaging.purposes.startBtn')}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PurposePicker;
